import React from 'react'
import { Chart as ChartModel } from 'generated/types'
import { BarChart } from 'components/charts/BarChart'
import { ChartData, ChartOptions } from 'chart.js'
import { chartColors } from './colors'

const HourlyHeatMapChart: React.FC<{ hours: ChartModel['hourlyUse'] }> = ({ hours }) => {
  const sortedHours = [...hours].sort((a, b) => a.hour - b.hour)
  const hourLabels = sortedHours.map((h) => {
    const meridiem = h.hour < 12 ? ' am' : 'pm'
    switch (h.hour) {
      case 0:
        return 'Midnight'
      case 12:
        return 'Noon'
      default:
        return `${((h.hour + 23) % 12) + 1}${meridiem}`
    }
  })
  const checkinCount = sortedHours.map((s) => s.count)

  const { lowThreshold, highThreshold } = thresholds(checkinCount)

  const checkinDataSets = checkinCount.map((count) => {
    if (count > Math.ceil(highThreshold)) {
      return { low: null, normal: null, high: count }
    }
    if (count < Math.floor(lowThreshold)) {
      return { low: count, normal: null, high: null }
    }
    return { low: null, normal: count, high: null }
  })

  const options: ChartOptions = {
    legend: { display: false },
    tooltips: {
      callbacks: {
        label: (({ label, index, datasetIndex }) => {
          if (datasetIndex !== 0 || index === undefined) {
            return ''
          }
          const checkinLabel = checkinCount[index] === 1 ? 'checkin' : 'checkins'
          return `${label} - ${checkinCount[index]} ${checkinLabel}`
        }),
        title: (items) => {
          const { index } = items[0]
          if (index === undefined) {
            return ''
          }
          if (checkinDataSets[index].low) {
            return 'Low utilization'
          }
          if (checkinDataSets[index].high) {
            return 'High utilization'
          }
          return 'Typical utilization'
        },
      },
    },
    scales: {
      xAxes: [
        {
          stacked: true,
          scaleLabel: {
            labelString: 'Hour',
          },
          ticks: {
            maxTicksLimit: 7,
            maxRotation: 0,
          },
        },
      ],
      yAxes: [
        {
          stacked: true,
        },
      ],
    },
  }

  const data: ChartData = {
    labels: hourLabels,
    datasets: [
      {
        data: checkinDataSets.map((s) => s.low),
        backgroundColor: chartColors.good,
      },
      {
        data: checkinDataSets.map((s) => s.normal),
      },
      {
        data: checkinDataSets.map((s) => s.high),
        backgroundColor: chartColors.bad,
      },
    ],
  }

  return (
    <BarChart data={data} options={options} height={50} />
  )
}

const thresholds = (checkinCounts: number[]) => {
  const { stdev, average } = stats(checkinCounts)
  return {
    highThreshold: average + stdev,
    lowThreshold: average - stdev,
  }
}

const average = (values: number[]) => {
  const sum = values.reduce((last, value) => {
    return last + value
  }, 0)
  return sum / values.length
}

const stats = (values: number[]) => {
  const avg = average(values)

  const squareDiffs = values.map((value) => {
    const diff = value - avg
    return diff * diff
  })

  const avgSquareDiff = average(squareDiffs)

  return {
    average: avg,
    stdev: Math.sqrt(avgSquareDiff),
  }
}

export default HourlyHeatMapChart
